import { ARTICLE_ENTITY_PREFIX_MAP } from '../../pages/articles/articlesConfigDefaults';
import { ACHATS_ENTITY_PREFIX_MAP } from '../../pages/achats/achatsConfigDefaults';
import { SALARIES_ENTITY_PREFIX_MAP } from '../../pages/salaires/salariesConfigDefaults';
import { HR_ENTITY_PREFIX_MAP } from '../../pages/ressources-humaines/hrConfigDefaults';

/**
 * Next entity reference (رمز) — CAT-001, PRD-002, etc.
 * Prefers PREFIX-NNN; falls back to trailing digits in existing values.
 */
export function generateEntityReference(prefix, records, getRef) {
  const safePrefix = String(prefix || 'REF').trim() || 'REF';
  const list = Array.isArray(records) ? records : [];
  const readRef = typeof getRef === 'function' ? getRef : (r) => r;

  let maxNumber = 0;
  for (const record of list) {
    const raw = String(readRef(record) ?? '').trim();
    if (!raw) continue;

    const prefixed = raw.match(new RegExp(`^(?:SA-)?${safePrefix}-(\\d+)$`, 'i'));
    if (prefixed) {
      maxNumber = Math.max(maxNumber, parseInt(prefixed[1], 10) || 0);
      continue;
    }

    const trailing = raw.match(/(\d+)$/);
    if (trailing) {
      maxNumber = Math.max(maxNumber, parseInt(trailing[1], 10) || 0);
    }
  }

  return `${safePrefix}-${String(maxNumber + 1).padStart(3, '0')}`;
}

/** Resolve prefix from module config, then settings.entityPrefixes, then fallback. */
export function getEntityPrefix(settings, key, fallback) {
  const inventoryPrefixMap = {
    warehouse: 'warehouseReferencePrefix',
    stockMovement: 'movementReferencePrefix',
    stockReservation: 'reservationReferencePrefix',
    transfer: 'transferReferencePrefix',
    adjustment: 'adjustmentReferencePrefix',
    physicalInventory: 'physicalInventoryReferencePrefix',
    lot: 'lotReferencePrefix',
    valuation: 'valuationReferencePrefix',
    replenishment: 'reorderReferencePrefix',
  };
  const crmPrefixMap = {
    lead: 'leadReferencePrefix',
    contact: 'contactReferencePrefix',
    opportunity: 'opportunityReferencePrefix',
    campaign: 'campaignReferencePrefix',
    interaction: 'interactionReferencePrefix',
    salesGoal: 'salesGoalReferencePrefix',
  };
  const ventesPrefixMap = {
    priceRule: 'priceRuleReferencePrefix',
    salesOrder: 'orderReferencePrefix',
    sale: 'saleReferencePrefix',
    delivery: 'deliveryReferencePrefix',
    salesReturn: 'returnReferencePrefix',
    commission: 'commissionReferencePrefix',
  };

  const ventesKey = ventesPrefixMap[key];
  const fromVentes = ventesKey ? settings?.ventes?.[ventesKey] : null;
  if (fromVentes) return String(fromVentes).trim() || fallback;

  const articlesKey = ARTICLE_ENTITY_PREFIX_MAP[key];
  const fromArticles = articlesKey ? settings?.articles?.[articlesKey] : null;
  if (fromArticles) return String(fromArticles).trim() || fallback;

  const achatsKey = ACHATS_ENTITY_PREFIX_MAP[key];
  const fromAchats = achatsKey ? settings?.achats?.[achatsKey] : null;
  if (fromAchats) return String(fromAchats).trim() || fallback;

  const treasuryPrefixMap = {
    voucher: 'voucherPrefix',
    transfer: 'transferPrefix',
    closure: 'closurePrefix',
    caisseReconciliation: 'reconciliationPrefix',
  };
  const treasuryKey = treasuryPrefixMap[key];
  const fromTreasury = treasuryKey ? settings?.treasury?.[treasuryKey] : null;
  if (fromTreasury) return String(fromTreasury).trim() || fallback;

  const inventoryKey = inventoryPrefixMap[key];
  const fromInventory = inventoryKey ? settings?.inventory?.[inventoryKey] : null;
  if (fromInventory) return String(fromInventory).trim() || fallback;

  const crmKey = crmPrefixMap[key];
  const fromCrm = crmKey ? settings?.crm?.[crmKey] : null;
  if (fromCrm) return String(fromCrm).trim() || fallback;

  const salariesKey = SALARIES_ENTITY_PREFIX_MAP[key];
  const fromSalaries = salariesKey ? settings?.salaries?.[salariesKey] : null;
  if (fromSalaries) return String(fromSalaries).trim() || fallback;

  const hrKey = HR_ENTITY_PREFIX_MAP[key];
  const fromHr = hrKey ? settings?.hr?.[hrKey] : null;
  if (fromHr) return String(fromHr).trim() || fallback;

  const bankingPrefixMap = {
    bankTransfer: 'transferReferencePrefix',
    bankCheck: 'checkReferencePrefix',
    bankStatement: 'statementReferencePrefix',
    bankAccount: 'accountReferencePrefix',
    bankEntry: 'entryReferencePrefix',
  };
  const bankingKey = bankingPrefixMap[key];
  const fromBanking = bankingKey
    ? (settings?.banking?._ui?.[bankingKey] ?? settings?.banking?.[bankingKey])
    : null;
  if (fromBanking) return String(fromBanking).trim() || fallback;

  return (
    settings?.facturation?.entityPrefixes?.[key]
    || settings?.crm?.entityPrefixes?.[key]
    || settings?.configuration?.entityPrefixes?.[key]
    || fallback
  );
}
